/**
 * m3 echo Frontend - Cutoff Detail Dialog
 *
 * Shows full details of a cutoff log entry.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { CutoffLogItem } from '../../services/admin';

interface CutoffDetailDialogProps {
  open: boolean;
  onClose: () => void;
  item: CutoffLogItem | null;
}

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';
  const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
  return new Date(utcDateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getLayerColor = (layer: string): 'primary' | 'secondary' | 'warning' | 'info' | 'error' => {
  switch (layer) {
    case 'L3': return 'primary';
    case 'L5': return 'secondary';
    case 'L6': return 'warning';
    case 'zyte': return 'info';
    case 'grounding': return 'error';
    default: return 'primary';
  }
};

const CutoffDetailDialog: React.FC<CutoffDetailDialogProps> = ({ open, onClose, item }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!item) return null;

  const truncationPercent = Math.round((item.chars_lost / item.original_length) * 100);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        Cutoff Detail #{item.id}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Layer & Field */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip label={item.layer} color={getLayerColor(item.layer)} size="small" />
            <Typography variant="body1" fontWeight="medium">
              {item.field_name}
            </Typography>
          </Box>

          {/* Truncation Bar */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {item.cutoff_limit.toLocaleString('de-DE')} / {item.original_length.toLocaleString('de-DE')} Zeichen
              </Typography>
              <Typography variant="caption" color="error.main" fontWeight="bold">
                {truncationPercent}% verloren
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={100 - truncationPercent}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          {/* Stats */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Limit</Typography>
              <Typography variant="body2">{item.cutoff_limit.toLocaleString('de-DE')} Zeichen</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Original</Typography>
              <Typography variant="body2">{item.original_length.toLocaleString('de-DE')} Zeichen</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Verloren</Typography>
              <Typography variant="body2" color="error.main">{item.chars_lost.toLocaleString('de-DE')} Zeichen</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Zeitpunkt</Typography>
              <Typography variant="body2">{formatDateTime(item.created_at)}</Typography>
            </Box>
          </Box>

          {/* IDs */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {item.job_id && (
              <Box>
                <Typography variant="caption" color="text.secondary">Job ID</Typography>
                <Typography variant="body2">{item.job_id}</Typography>
              </Box>
            )}
            {item.project_id && (
              <Box>
                <Typography variant="caption" color="text.secondary">Projekt ID</Typography>
                <Typography variant="body2">{item.project_id}</Typography>
              </Box>
            )}
            {(item.tenant_name || item.tenant_id) && (
              <Box>
                <Typography variant="caption" color="text.secondary">Mandant</Typography>
                <Typography variant="body2">{item.tenant_name || item.tenant_id}</Typography>
              </Box>
            )}
          </Box>

          {/* Prompt Text */}
          {item.prompt_text && (
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Prompt (Layer 1)
              </Typography>
              <Paper
                sx={{
                  p: 1.5,
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #90caf9',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    m: 0,
                  }}
                >
                  {item.prompt_text}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Content Preview */}
          {item.content_preview && (
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Originalinhalt
              </Typography>
              <Paper
                sx={{
                  p: 1.5,
                  backgroundColor: 'grey.50',
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    m: 0,
                  }}
                >
                  {item.content_preview}
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CutoffDetailDialog;
